import { observer } from "mobx-react-lite";
import { FunctionComponent, useContext } from "react";

import {
  IStyleFunctionOrObject,
  Stack,
  StateBlock,
  StateBlockStyleProps,
  StateBlockStyles
} from "@bps/fluent-ui";
import { DiagnosisList } from "@modules/clinical/screens/patient-record/components/SOTAP/discharge-form/DiagnosisList.tsx";
import { DischargeSummaryProvider } from "@modules/clinical/screens/patient-record/components/SOTAP/discharge-form/DischargeSummaryProvider.tsx";
import { useCardFormLayoutContext } from "@ui-components/card-form-layout/context/CardFormLayoutHelperContext.ts";

import {
  ClaimDischargeCardIds,
  ClaimDischargeSideRailLabels
} from "../../claim/components/ClaimFormEnums.ts";
import { ClaimCard } from "../../shared-components/ClaimCard.tsx";
import { DischargeContext } from "../context/DischargeContext.ts";

export const ClaimDischargeSummarySection: FunctionComponent = observer(() => {
  const { selectedMenuItemId } = useCardFormLayoutContext();
  const {
    claim,
    selectedClaimDischarge,
    getDiagnoses,
    totalConsultCount,
    haveMultipleDischarges,
    getDischargeDescription
  } = useContext(DischargeContext);

  const { primaryDiagnoses, additionalDiagnoses } = getDiagnoses();

  const stateBlockStyles: IStyleFunctionOrObject<
    StateBlockStyleProps,
    StateBlockStyles
  > = {
    root: {
      padding: "8px 16px"
    }
  };

  return (
    <ClaimCard
      id={ClaimDischargeCardIds.Summary}
      heading={ClaimDischargeSideRailLabels.Summary}
      iconName="FormLibraryMirrored"
      openOnRender={
        selectedMenuItemId === ClaimDischargeCardIds.Summary ||
        selectedMenuItemId === undefined
      }
    >
      <Stack
        tokens={{
          childrenGap: 8
        }}
      >
        {selectedClaimDischarge?.providerId && (
          <DischargeSummaryProvider
            providerId={selectedClaimDischarge.providerId}
            providerBusinessRole={selectedClaimDischarge.businessRole}
          />
        )}
        <DiagnosisList
          primaryDiagnoses={primaryDiagnoses}
          additionalDiagnoses={additionalDiagnoses}
        />
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          {claim.referralProvider && (
            <StateBlock
              styles={stateBlockStyles}
              labelText="Referral provider"
              descText={claim.referralProvider}
            />
          )}
          <StateBlock
            styles={stateBlockStyles}
            labelText="Claim number"
            descText={claim.claimNumber ?? "Not lodged"}
          />
          <StateBlock
            styles={stateBlockStyles}
            labelText="Total consults"
            descText={`${totalConsultCount} total ${
              totalConsultCount > 1 ? "visits" : "visit"
            }`}
          />
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <StateBlock
            styles={stateBlockStyles}
            labelText="Injury date"
            descText={
              claim.accidentDate
                ? claim.accidentDate.toDayDefaultFormat()
                : "Nil"
            }
          />
          <StateBlock
            styles={stateBlockStyles}
            labelText="Initial consult"
            descText={
              claim.initialConsultDate
                ? claim.initialConsultDate.toDayDefaultFormat()
                : "Nil"
            }
          />
          {haveMultipleDischarges && (
            <StateBlock
              styles={stateBlockStyles}
              labelText="Discharge"
              descText={getDischargeDescription()}
            />
          )}
        </Stack>
      </Stack>
    </ClaimCard>
  );
});
